import type React from "react";
import { useCallback } from "react";

import type { ChatDto } from "@/api/types";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { Card } from "@/design-system/Card";
import { Spinner } from "@/design-system/Spinner";
import { useBool } from "@/hooks/useBool";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { ChatListItem } from "@/modules/chats/components/ChatListItem";
import { MAX_PINNED_CHATS } from "@/queries/chats/constants";
import { useTranslation } from "@/translations";

interface ChatListViewProps {
  chats: ChatDto[];
  onLoadMoreChats: () => void;
  isLoadingMoreChats: boolean;
  hasMoreChats: boolean;
}

export function ChatListView({
  chats,
  isLoadingMoreChats,
  hasMoreChats,
  onLoadMoreChats,
}: ChatListViewProps): React.ReactNode {
  const [isPinChatLimitWarningModalOpened, pinChatLimitWarningModalHandler] = useBool(false);

  const { t } = useTranslation();
  const refLoader = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (!isLoadingMoreChats && hasMoreChats) {
        onLoadMoreChats();
      }
    }, [onLoadMoreChats, hasMoreChats, isLoadingMoreChats]),
  });

  const canPinMoreChat = chats.filter((chat) => Boolean(chat.pinnedAt)).length < MAX_PINNED_CHATS;

  if (chats.length === 0) {
    return (
      <Card>
        <span className="text-caption text-text-secondary">{t("page.chats.no-chats")}</span>
      </Card>
    );
  }

  return (
    <>
      <Card as="section" density="flush" scroll="content">
        <ul data-testid="chat-list" className="overflow-hidden">
          {chats.map((chat) => (
            <ChatListItem
              key={chat.id}
              chat={chat}
              canPinMoreChat={canPinMoreChat}
              onPinLimitExceeded={pinChatLimitWarningModalHandler.setTrue}
            />
          ))}
        </ul>
        {hasMoreChats && (
          <footer className="flex justify-center p-space-md" ref={refLoader}>
            <Spinner size="md" />
          </footer>
        )}
      </Card>
      <ConfirmModal
        isOpened={isPinChatLimitWarningModalOpened}
        title={t("page.chats.pin-chat-limit-warning-modal.title", {
          count: MAX_PINNED_CHATS,
        })}
        isLoading={false}
        tone="primary"
        onReject={pinChatLimitWarningModalHandler.setFalse}
        onOpenChange={pinChatLimitWarningModalHandler.set}
        onResolve={() => pinChatLimitWarningModalHandler.setFalse()}
        resolveBtnProps={{
          text: t("common.action.got-it"),
        }}
        rejectBtnProps={{
          hide: true,
        }}
        data-testid="pin-chat-limit-warning-modal"
        shouldCloseOnEsc
      />
    </>
  );
}
